import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, Users, Zap, Star, Globe, Shield, Heart, Info, UserPlus } from 'lucide-react';

export default function HowItWorks() {
  const { t } = useTranslation();

  const steps = [
    {
      number: '01',
      title: t('howItWorks.step1Title'),
      description: t('howItWorks.step1Description'),
      icon: <Users className="w-8 h-8 text-values-icon" />
    },
    {
      number: '02',
      title: t('howItWorks.step2Title'),
      description: t('howItWorks.step2Description'),
      icon: <Zap className="w-8 h-8 text-values-icon" />
    },
    {
      number: '03',
      title: t('howItWorks.step3Title'),
      description: t('howItWorks.step3Description'),
      icon: <Star className="w-8 h-8 text-values-icon" />
    }
  ];

  const features = [
    {
      icon: <Globe className="w-8 h-8 text-values-icon" />,
      title: t('howItWorks.feature1Title'),
      description: t('howItWorks.feature1Description')
    },
    {
      icon: <Shield className="w-8 h-8 text-values-icon" />,
      title: t('howItWorks.feature2Title'),
      description: t('howItWorks.feature2Description')
    },
    {
      icon: <Heart className="w-8 h-8 text-values-icon" />,
      title: t('howItWorks.feature3Title'),
      description: t('howItWorks.feature3Description')
    }
  ];


  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 divide-y divide-zinc-200 dark:divide-zinc-800 space-y-8">
        {/* Hero */}
        <section className="py-10 px-4">
          <div className="container">
            <div className="text-center px-4">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-primary-text">
                {t('howItWorks.pageTitle')}
              </h1>
              <p className="text-xl max-w-3xl mx-auto opacity-90 text-secondary-text">
                {t('howItWorks.heroDescription')}
              </p>
            </div>
          </div>
        </section>

        {/* Video */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-primary-text">{t('howItWorks.videoTitle')}</h2>
              <p className="text-lg text-secondary-text max-w-2xl mx-auto">{t('howItWorks.videoDescription')}</p>
            </div>
            <div className="max-w-4xl mx-auto">
              <video className="w-full rounded-2xl shadow-lg" controls muted preload="metadata">
                <source src="/HowItWorksVideo.mp4" type="video/mp4" />
                {t('howItWorks.videoNotSupported')}
              </video>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-primary-text">{t('howItWorks.stepsTitle')}</h2>
              <p className="text-lg text-secondary-text max-w-2xl mx-auto">{t('howItWorks.stepsDescription')}</p>
            </div>

            {/* Desktop: Grid with absolute arrows */}
            <div className="hidden md:block relative">
              <div className="grid grid-cols-3 gap-16">
                {steps.map((step, index) => (
                  <div key={index} className="relative group">
                    <div className="bg-card-bg values-card card-hover rounded-2xl p-8 h-full">
                      <div className="flex items-center mb-6">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center values-icon mr-4">
                          {step.icon}
                        </div>
                        <span className="text-6xl font-bold step-number">{step.number}</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-4 text-primary-text">{step.title}</h3>
                      <p className="text-secondary-text leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop arrows */}
              <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-8 h-8 arrow-icon" />
              </div>
              <div className="absolute top-1/2 left-2/3 -translate-x-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-8 h-8 arrow-icon" />
              </div>
            </div>

            {/* Mobile: Flex column with inline arrows */}
            <div className="md:hidden flex flex-col items-center gap-4">
              {steps.map((step, index) => (
                <div key={index} className="w-full">
                  <div className="bg-card-bg values-card card-hover rounded-2xl p-6 h-full">
                    <div className="flex items-center mb-6">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center values-icon mr-4">
                        {step.icon}
                      </div>
                      <span className="text-5xl font-bold step-number">{step.number}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-4 text-primary-text">{step.title}</h3>
                    <p className="text-secondary-text leading-relaxed text-sm">{step.description}</p>
                  </div>
                  {/* Arrow between cards (not after last) */}
                  {index < steps.length - 1 && (
                    <div className="flex justify-center py-3">
                      <ArrowDown className="w-6 h-6 arrow-icon" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-primary-text">{t('howItWorks.featuresTitle')}</h2>
              <p className="text-lg text-secondary-text max-w-2xl mx-auto">{t('howItWorks.featuresDescription')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="text-center">
                  <div className="p-8 rounded-lg shadow-sm bg-card-bg values-card card-hover h-full">
                    <div className="mb-4 flex justify-center">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-primary-text mb-3">{feature.title}</h3>
                    <p className="text-secondary-text">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>



        {/* CTA */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center p-8">
              <h2 className="text-3xl font-bold mb-4 text-primary-text">{t('howItWorks.ctaTitle')}</h2>
              <p className="text-xl text-secondary-text mb-8 max-w-2xl mx-auto">{t('howItWorks.ctaDescription')}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="cta-brand inline-flex items-center justify-center gap-2 px-6 py-3"
                  data-variant="primary"
                >
                  <UserPlus className="w-5 h-5" aria-hidden="true" />
                  {t('howItWorks.ctaButtonPrimary')}
                </Link>
                <Link
                  to="/about"
                  className="cta-brand inline-flex items-center justify-center gap-2 px-6 py-3"
                  data-variant="primary"
                >
                  <Info className="w-5 h-5" aria-hidden="true" />
                  {t('howItWorks.ctaButtonSecondary')}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
